import { Divider, Form, Tag } from 'antd';
import AppTypography from '../../typography/Typography';
import { FIELD_REGISTRY, getFieldFormItemProps } from './fieldRegistry.jsx';
import { resolveOptions } from './utils/popupUtils';
import { securityValidationRule } from '../../form/inputSecurity';

// Popup field types that accept free text — these get the same central
// input-security rule (script/HTML/SQL/command-injection blocking) the
// AddFormV1/EditFormV1 engines apply to every form field. New popup
// definitions inherit it automatically.
const TEXT_LIKE_POPUP_TYPES = new Set(['input', 'text', 'textarea', 'mention-textarea']);

/**
 * Renders the form section of DynamicPopup, including:
 *   - The "Already Assigned People" tag list (for others-assign)
 *   - All form fields via FIELD_REGISTRY
 *
 * @param {Object} props
 * @param {Object} props.form - Ant Design form instance
 * @param {Array} props.fields
 * @param {Object} props.initialValues
 * @param {Object} props.dynamicOptions
 * @param {Object} props.popupContext
 * @param {*} props.extraData
 * @param {string[]} props.removedIds
 * @param {Function} props.setRemovedIds
 * @param {boolean} [props.showAlreadyAssigned]
 * @param {Function} props.onValuesChange
 * @param {Function} props.getSearchHandler
 * @param {Function} props.getMentionSearchHandler
 * @param {Object} props.mentionOptions
 */
export default function PopupFields({
  form,
  fields,
  initialValues,
  dynamicOptions,
  popupContext,
  extraData,
  removedIds,
  setRemovedIds,
  showAlreadyAssigned,
  onValuesChange,
  getSearchHandler,
  getMentionSearchHandler,
  mentionOptions,
}) {
  const hasAssigned = showAlreadyAssigned && extraData;
  if (!fields.length && !hasAssigned) return null;

  return (
    <>
      {hasAssigned && (
        <>
          <div className="dynamic-popup__already-assigned">
            <AppTypography variant="caption" color="secondary">Already Assigned People</AppTypography>
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(extraData?.recruitersData ?? [])
                .filter((recruiter) => {
                  const id = String(
                    recruiter?.user_id ?? recruiter?.id ?? recruiter?.userId ?? '',
                  );
                  return id && !removedIds.includes(id);
                })
                .map((recruiter) => {
                  const id = String(
                    recruiter?.user_id ?? recruiter?.id ?? recruiter?.userId ?? '',
                  );
                  const name =
                    recruiter?.name ??
                    recruiter?.user_name ??
                    recruiter?.userName ??
                    recruiter?.email ??
                    id;
                  return (
                    <Tag
                      key={id || name}
                      closable
                      onClose={() => {
                        if (!id) return;
                        setRemovedIds((prev) => Array.from(new Set([...prev, id])));
                      }}
                    >
                      {name}
                    </Tag>
                  );
                })}
            </div>
          </div>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}

      {fields.length > 0 && (
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onValuesChange={(_, allValues) => onValuesChange(allValues)}
        >
          {groupFieldsIntoRows(fields).map((rowFields) => renderRow(rowFields, (field) => {
            const options = dynamicOptions[field.name] ?? resolveOptions(field, popupContext);
            const onSearch = getSearchHandler(field);
            const onMentionSearch = getMentionSearchHandler(field);
            const currentMentionOpts = mentionOptions[field.name] ?? [];
            const formItemProps = getFieldFormItemProps(field.type);
            const Control = FIELD_REGISTRY[field.type];

            const rules = [];
            if (field.required) {
              rules.push({ required: true, message: `${field.label || 'This field'} is required` });
            }
            if (typeof field.validator === 'function') {
              rules.push({
                validator: (_, value) => {
                  const errorMessage = field.validator(value, popupContext);
                  return errorMessage ? Promise.reject(new Error(errorMessage)) : Promise.resolve();
                },
              });
            }
            if (TEXT_LIKE_POPUP_TYPES.has(field.type)) {
              rules.push(securityValidationRule({ label: field.label, field: field.name, type: field.type }));
            }

            return (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={rules}
                {...formItemProps}
              >
                {Control ? (
                  <Control
                    field={field}
                    options={options}
                    onSearch={onSearch}
                    mentionOpts={currentMentionOpts}
                    onMentionSearch={onMentionSearch}
                  />
                ) : null}
              </Form.Item>
            );
          }))}
        </Form>
      )}
    </>
  );
}

// ── Inline rows ───────────────────────────────────────────────────────────────
// Consecutive fields sharing the same `inlineRow` key render side-by-side on
// one line (e.g. the Approve Rate popup's Proposed Rate + Currency + Unit).
// Fields without inlineRow keep the default one-per-line layout, so every
// existing popup is unaffected. Generic — any popup definition can opt in.
function groupFieldsIntoRows(fields) {
  const rows = [];
  for (const field of fields) {
    const previous = rows[rows.length - 1];
    if (field.inlineRow && previous?.inlineRow === field.inlineRow) {
      previous.fields.push(field);
    } else {
      rows.push({ inlineRow: field.inlineRow, fields: [field] });
    }
  }
  return rows.map((row) => row.fields);
}

function renderRow(rowFields, renderField) {
  if (rowFields.length === 1) return renderField(rowFields[0]);
  return (
    <div
      key={`row-${rowFields.map((f) => f.name).join('-')}`}
      style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}
    >
      {rowFields.map((field, index) => (
        <div key={field.name} style={{ flex: index === 0 ? 2 : 1, minWidth: 0 }}>
          {renderField(field)}
        </div>
      ))}
    </div>
  );
}
